import React, { useState, useEffect, useRef, useCallback } from 'react'
import { graphql } from 'gatsby'
import { window, exists } from 'browser-monads'
import { useScrollPosition } from '@n8tb1t/use-scroll-position'
import { Row } from '../../../common/lib'
import {
  CoverImage,
  HelloBar,
  ScrollToTopButton,
  SeeAlso,
} from '../../../common/components'
import ArticlePageHelmet from './article/ArticlePageHelmet'
import ArticleStickyHeader from './article/ArticelSticyHeader'
import ArticleToc from './article/ArticleToc'
import ArticleContentSection from './article/ArticleContentSection'
import ArticleSidebar from './article/ArticleSidebar'
import styles from '../../../common/scss/content.module.scss'
import {
  replaceYear,
  formatDate,
  useIsIntersectingElement,
} from '../../../common/utils'
import '../../../common/scss/cheerio.scss'
import { GENERIC_CONSTANT } from '../../../common/utils/constants'
import { editArticle } from '../../../common/utils/editArticle'
import replaceText from '../../../common/utils/replaceText'

export default function ArticleTemplate({
  data: { datoCmsPsArticle: article = {}, sidebarLinksForInterviewAdvice = {}, allDatoCmsToken = {} },
  location,
  pageContext: { pathname },
}) {
  const tokens = allDatoCmsToken.edges
  const isAptitudeTest = location.pathname.match('aptitude-test')
  const helloBarInfo = {
    url:
      article.helloBarUrl ||
      article.heroButtonUrl ||
      (isAptitudeTest ? GENERIC_CONSTANT.hellobar_button_url : null),
    buttonText:
      replaceText(tokens, article.helloBarButtonText) ||
      (isAptitudeTest ? `Take a ${replaceText(tokens, article.title)} test!` : null),
    text:
      replaceText(tokens, article.helloBarText) ||
      (isAptitudeTest ? GENERIC_CONSTANT.practice_aptitude_text : null),
  }

  const faqs = article?.modularContent?.filter(
    (content) => content?.internal?.type === 'DatoCmsFaqItem',
  )

  const [stickyHeaderShown, setStickyHeaderShown] = useState('')
  const lastUpdatedRef = useRef(null)

  const handleEditArticle = useCallback((event) => {
    if (event.ctrlKey && event.key === 'b') {
      editArticle(287, article.originalId)
    }
  }, [])

  if (window.dataLayer) {
    window.dataLayer.push({ article, disableAds: article.disableAds })
  }
  const updatedAt = formatDate(article.meta.updatedAt, 'article')
  const canonicalUrl = `https://psychometric-success.com${location.pathname}`

  useScrollPosition(({ prevPos, currPos }) => {
    if (exists(window)) {
      if (!stickyHeaderShown && currPos.y < -650) {
        setStickyHeaderShown(true)
      }
      if (stickyHeaderShown && currPos.y > -650) {
        setStickyHeaderShown('')
      }
    }
  })

  useEffect(() => {
    window.addEventListener('keydown', handleEditArticle)
    return () => window.removeEventListener('keydown', handleEditArticle)
  }, [])

  // useEffect(() => {
  //   if (!eventListeners) {
  //     const handlePressedKey = (event) => {
  //       if (event.ctrlKey && event.key === 'b') {
  //         editArticle(287, article.originalId)
  //       }
  //     }
  //     window.addEventListener('keydown', handlePressedKey)
  //     setEventListeners(true)
  //   }
  // }, [
  //   isAptitudeTest,
  //   eventListeners,
  //   article.originalId,
  //   article.heroButtonUrl,
  //   article.helloBarUrl,
  //   article.helloBarButtonText,
  //   article.title,
  //   article.helloBarText,
  // ])

  if (window.dataLayer) {
    window.dataLayer.push({ event: 'componentDidMount' })
  }

  const isScrollToTopShown = !useIsIntersectingElement(lastUpdatedRef.current)

  const ShowHelloBar = (
    <>
      {helloBarInfo &&
        helloBarInfo.url &&
        helloBarInfo.text &&
        helloBarInfo.buttonText && (
          <HelloBar
            location={location}
            url={helloBarInfo.url}
            buttonText={helloBarInfo.buttonText}
            text={helloBarInfo.text}
          />
        )}
    </>
  )

  const ShowArticle = (
    <div className={`container-xxl ${styles.paddingTop}`}>
      <ArticleStickyHeader
        article={article}
        location={location}
        stickyHeaderShown={stickyHeaderShown}
        tokens={tokens}
      />
      <ArticlePageHelmet article={article} canonicalUrl={canonicalUrl} tokens={tokens} />
      <CoverImage coverImage={article.coverImage} title={article.title} />
      <Row>
        <ArticleToc article={article} faqs={faqs} location={location} tokens={tokens} />
        <ArticleContentSection
          faqs={faqs}
          article={article}
          location={location}
          pathname={pathname}
          updatedAt={updatedAt}
          lastUpdatedRef={lastUpdatedRef}
          tokens={tokens}
        />
        <ArticleSidebar
          location={location}
          article={article}
          faqs={faqs}
          sidebarLinksForInterviewAdvice={sidebarLinksForInterviewAdvice}
        />
      </Row>

      {/* NOTE: Scroll up feature (needs to be tested well) */}
      {/* <div className="scroll-to-top-container">
    <a className="scroll-to-top" href="#articleContents">
      <ArrowUpCircle />
    </a>
  </div> */}
      {article.seeAlso && <SeeAlso article={article} siteName={'Psychometric Success'} tokens={tokens} />}
      <ScrollToTopButton isScrollToTopShown={isScrollToTopShown} />
      {/* {exitIntentPopupShown && pageIsScrolled && (
      <ExitIntentPopup
        pathname={location.pathname}
        closePopup={() => setExitIntentPopupShown(false)}
        testUrl={article.heroButtonUrl || article.heroButtonUrl2}
      />
    )} */}
    </div>
  )

  return (
    <>
      {ShowHelloBar}
      {ShowArticle}
    </>
  )
}

export const query = graphql`
  query ($id: String!) {
    allDatoCmsToken {
      edges{
        node{
          key 
          value
          valueMarkdown{
            markdownNode{
              childMarkdownRemark{
                html
              }
            }
          }
        }
      }    
    }
    datoCmsPsArticle(id: { eq: $id }) {
      originalId
      title
      slug
      sources {
        publication
        publicationYear
        author
        link
      }
      metaTitle
      metaDescription
      meta {
        createdAt
        updatedAt
        publishedAt
        firstPublishedAt
      }
      language
      disableAds
      author {
        name
        picture {
          gatsbyImageData(
            width: 50
            height: 50
            layout: FIXED
            imgixParams: {
              auto: "compress, format, enhance"
              fit: "facearea"
              w: "50"
              h: "50"
              facepad: 3
            }
          )
        }
      }
      shortUrl
      helloBarButtonText
      helloBarText
      helloBarUrl
      heroText
      heroButtonText
      heroDisclaimer
      heroButtonUrl
      heroButtonText2
      heroButtonUrl2
      coverImage {
        gatsbyImageData(
          imgixParams: {
            auto: "format, compress, enhance"
            fit: "crop"
            h: "400"
            w: "1116"
          }
          layout: FULL_WIDTH
          sizes: "(max-width: 1116px) calc(100vw - 120px), 1116px"
          breakpoints: [200, 400, 900, 1116]
        )
        ogImage: url(imgixParams: { auto: "enhance", w: "1200", h: "630" })
      }
      seeAlso {
        title
        slug
        shortUrl
        coverImage {
          gatsbyImageData(
            imgixParams: { auto: "compress, format, enhance", fit: "crop" }
            height: 150
            width: 220
          )
        }
        category {
          id
          title
          slug
          category {
            id
            title
            slug
          }
        }
      }
      category {
        title
        slug
        category {
          title
          slug
        }
      }
      modularContent {
        ... on DatoCmsProCon {
          internal {
            type
          }
          proNode {
            childMarkdownRemark {
              id
              html
            }
          }
          conNode {
            childMarkdownRemark {
              id
              html
            }
          }
        }
        ... on DatoCmsHowtostep {
          title
          internal {
            type
          }
          body {
            ... on DatoCmsCallToActionButton {
              internal {
                type
              }
              link
              text
            }
            ... on DatoCmsHtml {
              internal {
                type
              }
              html
            }
            ... on DatoCmsImage {
              internal {
                type
              }
              image {
                gatsbyImageData(
                  layout: FULL_WIDTH
                  placeholder: BLURRED
                  breakpoints: [200, 508, 721, 991, 1116]
                )
                url(
                  imgixParams: {
                    q: 70
                    w: "1000"
                    auto: "[format, compress, enhance]"
                  }
                )
                title
                alt
              }
            }
            ... on DatoCmsMarkdown {
              id
              internal {
                type
              }
              markdownNode {
                childMarkdownRemark {
                  html
                  rawMarkdownBody
                }
              }
            }
            ... on DatoCmsHtml {
              internal {
                type
              }
              html
            }
            ... on DatoCmsRawHtmlScript {
              internal {
                type
              }
              rawHtml
            }
          }
          id
          image {
            gatsbyImageData(
              layout: FULL_WIDTH
              placeholder: BLURRED
              breakpoints: [200, 508, 721, 991, 1116]
            )
            url(
              imgixParams: {
                q: 70
                w: "1000"
                auto: "[format, compress, enhance]"
              }
            )
            title
            alt
          }
        }
        ... on DatoCmsExampleQuestion {
          internal {
            type
          }
          questionNode {
            childMarkdownRemark {
              html
            }
          }
          answerExplanationNode {
            childMarkdownRemark {
              html
            }
          }
        }
        ... on DatoCmsMarkdown {
          internal {
            type
          }
          markdownNode {
            childMarkdownRemark {
              html
              tableOfContents(maxDepth: 3)
            }
          }
        }
        ... on DatoCmsBigQuote {
          internal {
            type
          }
          bodyNode {
            childMarkdownRemark {
              html
              tableOfContents(maxDepth: 3)
            }
          }
          style
        }
        ... on DatoCmsProduct {
          internal {
            type
          }
          name
          subheading
          label
          bodyString: body
          image {
            url(
              imgixParams: {
                q: 70
                w: "400"
                auto: "[compress, format, enhance]"
              }
            )
            gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
          }
          buttonText
          buttonUrl
        }
        ... on DatoCmsHtml {
          internal {
            type
          }
          html
        }
        ... on DatoCmsCallToActionButton {
          internal {
            type
          }
          link
          text
        }
        ... on DatoCmsRawHtmlScript {
          internal {
            type
          }
          rawHtml
        }
        ... on DatoCmsImage {
          internal {
            type
          }
          image {
            url(
              imgixParams: {
                q: 70
                w: "1000"
                auto: "[format, compress, enhance]"
              }
            )
            gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
            title
            alt
          }
        }
        ... on DatoCmsTop10item {
          modularContent {
            ... on DatoCmsProCon {
              internal {
                type
              }
              proNode {
                childMarkdownRemark {
                  id
                  html
                }
              }
              conNode {
                childMarkdownRemark {
                  id
                  html
                }
              }
            }
            ... on DatoCmsMarkdown {
              id
              internal {
                type
              }
              markdownNode {
                childMarkdownRemark {
                  html
                  tableOfContents(maxDepth: 3)
                }
              }
            }
          }
          title
          ctaUrl
          published
          bodyNode {
            childMarkdownRemark {
              html
            }
          }
          internal {
            type
          }
        }
        ... on DatoCmsHowto {
          title
          intro {
            markdownNode {
              childMarkdownRemark {
                html
                rawMarkdownBody
              }
            }
          }
          internal {
            type
          }
          howtosteps {
            title
            internal {
              type
            }
            body {
              ... on DatoCmsCallToActionButton {
                internal {
                  type
                }
                link
                text
              }
              ... on DatoCmsHtml {
                internal {
                  type
                }
                html
              }
              ... on DatoCmsImage {
                internal {
                  type
                }
                image {
                  gatsbyImageData(
                    layout: FULL_WIDTH
                    placeholder: BLURRED
                    breakpoints: [200, 508, 721, 991, 1116]
                  )
                  url(
                    imgixParams: {
                      q: 70
                      w: "1000"
                      auto: "[format, compress, enhance]"
                    }
                  )
                  title
                  alt
                }
              }
              ... on DatoCmsMarkdown {
                id
                internal {
                  type
                }
                markdownNode {
                  childMarkdownRemark {
                    html
                    rawMarkdownBody
                  }
                }
              }
              ... on DatoCmsHtml {
                internal {
                  type
                }
                html
              }
              ... on DatoCmsRawHtmlScript {
                internal {
                  type
                }
                rawHtml
              }
              ... on DatoCmsBigQuote {
                internal {
                  type
                }
                bodyNode {
                  childMarkdownRemark {
                    html
                    tableOfContents(maxDepth: 3)
                  }
                }
                style
              }
            }
            id
            image {
              gatsbyImageData(
                layout: FULL_WIDTH
                placeholder: BLURRED
                breakpoints: [200, 508, 721, 991, 1116]
              )
              url(
                imgixParams: {
                  q: 70
                  w: "1000"
                  auto: "[format, compress, enhance]"
                }
              )
              title
              alt
            }
          }
        }
        ... on DatoCmsFaqItem {
          id
          question
          answerNode {
            childMarkdownRemark {
              html
            }
          }
          internal {
            type
          }
        }
        ... on DatoCmsTable {
          internal {
            type
          }
          table {
            tr {
              ... on DatoCmsTableColumn {
                internal {
                  type
                }
                colspan
                th
                td {
                  ... on DatoCmsRawHtmlScript {
                    internal {
                      type
                    }
                    rawHtml
                  }
                  ... on DatoCmsMarkdown {
                    id
                    internal {
                      type
                    }
                    markdownNode {
                      childMarkdownRemark {
                        html
                        rawMarkdownBody
                      }
                    }
                  }
                  ... on DatoCmsHtml {
                    internal {
                      type
                    }
                    html
                  }
                  ... on DatoCmsCallToActionButton {
                    internal {
                      type
                    }
                    link
                    text
                  }
                }
              }
            }
          }
        }
        ... on DatoCmsDoDont {
          internal {
            type
          }
          doNode {
            childMarkdownRemark {
              id
              html
            }
          }
          dontNode {
            childMarkdownRemark {
              id
              html
            }
          }
        }
      }
    }
    sidebarLinksForInterviewAdvice: allDatoCmsSidebar(
      filter: { thePageWantsToDisplay: { eq: "interview-advice" } }
    ) {
      edges {
        node {
          id
          text
          url
        }
      }
    }
  }
`
